import { watch } from 'vue';
import { storeToRefs } from 'pinia';
import { split } from 'lodash-es';

import { countryProfileService } from './services/country_profiles';
import { wisOccupationsService } from './services/wis_occupations';
import { tariffsService } from './services/tariffs';
import { salesPortalService } from './services/sales_portal';
import { wisService } from './services/wis';

import { useApiResponsesStore } from './store/responses';
import { useChooserFormStore } from './store/forms/chooser';
import { useTariffsStore } from './store/tariffs';
import { useTranslationsStore } from './store/translations';

/**
 * Fetch data from API endpoints on variable changes
 */
export const watchers = {
    setup: function () {
        const formsStore = useChooserFormStore();
        const { formData } = storeToRefs(formsStore);

        const apiResponseStore = useApiResponsesStore();
        const { countryResponses, regionResponses, occupationResponses } =
            storeToRefs(apiResponseStore);

        const translationStore = useTranslationsStore();
        const { locale } = storeToRefs(translationStore);

        const tariffsStore = useTariffsStore();
        const { yearly } = storeToRefs(tariffsStore);
        const { updateTariffs, updateCountryTariffs } = tariffsStore;

        // Locale watcher
        watch(
            () => locale.value,
            (value) => {
                // Get translations
                tariffsService.getTranslations(value).then((response) => {
                    const translations = Object.assign(
                        {},
                        ...response.map((translation) => {
                            return {
                                [translation.identifier]: translation.value,
                            };
                        })
                    );
                    translationStore.$patch({
                        translations: translations,
                    });
                });

                // Get occupations
                wisOccupationsService(locale.value)
                    .getLevelOneOccupations()
                    .then((response) => {
                        apiResponseStore.$patch((state) => {
                            state.occupationResponses.levelOneOccupations =
                                response;
                        });
                    });

                // Convert alpha to numeric country code
                const alphaCountryCode = split(value, '_').at(-1);
                tariffsService
                    .convertCountryCode(alphaCountryCode)
                    .then((response) => {
                        apiResponseStore.$patch((state) => {
                            state.countryResponses.numericCode =
                                response['numeric_code'];
                        });
                    });
            }
        );

        // Region watcher
        watch(
            () => formData.value.region.code,
            (value) => {
                if (value) {
                    // Get annual avg living wages
                    salesPortalService
                        .getYearlyAverageLivingWages(value)
                        .then((data) => {
                            apiResponseStore.$patch((state) => {
                                // annualLivingWages
                                state.regionResponses.annualLivingWages = data;
                            });
                        });
                    // Get quarterly living wages
                    salesPortalService
                        .getQuarterlyLivingWages(value)
                        .then((data) => {
                            apiResponseStore.$patch((state) => {
                                state.regionResponses.quarterlyLivingWages =
                                    data;
                            });
                            tariffsStore.$patch((state) => {
                                state.yearly.current = data.period.year.value;
                            });
                        });
                } else {
                    // Clear annual and quarterly living wages
                    apiResponseStore.$patch((state) => {
                        state.regionResponses.annualLivingWages = null;
                        state.regionResponses.annualLivingWages = null;
                    });
                    tariffsStore.$patch((state) => {
                        state.yearly.current = null;
                    });
                }
            }
        );

        // Numeric country code watcher
        watch(
            () => countryResponses.value.numericCode,
            (value) => {
                // Get country name
                wisService.getCountryName(value).then((translations) => {
                    apiResponseStore.$patch((state) => {
                        state.countryResponses.name =
                            translations[locale.value];
                    });
                });
                // Get work hours
                wisService.getWorkHours(value).then((response) => {
                    apiResponseStore.$patch((state) => {
                        state.countryResponses.workingHours =
                            response['mappings'];
                    });
                });
                // Get country profile
                countryProfileService.getCountry(value).then((response) => {
                    apiResponseStore.$patch((state) => {
                        state.countryResponses.countryProfile = response;
                    });
                });
                // Get occupational costs
                tariffsService.occupationCosts(value).then((response) => {
                    apiResponseStore.$patch((state) => {
                        state.countryResponses.occupationMonthlyCosts =
                            response['data'];
                    });
                });
            }
        );

        // Locale and numeric country code watcher
        watch(
            () => {
                return  [
                    locale.value,
                    countryResponses.value.numericCode,
                ];
            },
            ([locale, numericCountryCode ]) => {
                if (locale && numericCountryCode) {
                    // Get regions
                    salesPortalService
                        .getRegions(numericCountryCode, locale)
                        .then((response) => {
                            apiResponseStore.$patch((state) => {
                                state.regionResponses.regions = response;
                            });
                        });
                }
            }
        );

        // Occupations level one watcher
        watch(
            () => formData.value.occupation.levelOne,
            (value) => {
                if (value && value.id) {
                    // Get GIG GIZ occupation data
                    wisOccupationsService(locale.value)
                        .getLevelTwoOccupations(value.id)
                        .then((response) => {
                            apiResponseStore.$patch((state) => {
                                state.occupationResponses.levelTwoOccupations =
                                    response;
                            });
                        });
                } else {
                    // Clear GIG GIZ occupation data
                    apiResponseStore.$patch((state) => {
                        state.occupationResponses.gigData = null;
                    });
                }
            }
        );

        // Occupations level two watcher
        watch(
            () => formData.value.occupation.levelTwo,
            (value) => {
                if (value && value.id) {
                    // Get GIG GIZ occupation data
                    wisService.occupationsGig(value.id).then((response) => {
                        apiResponseStore.$patch((state) => {
                            state.occupationResponses.gigData =
                                response['mappings'];
                        });
                    });
                } else {
                    // Clear GIG GIZ occupation data
                    apiResponseStore.$patch((state) => {
                        state.occupationResponses.gigData = null;
                    });
                }
            }
        );

        watch(
            [
                countryResponses.value,
                regionResponses.value,
            ],
            ([countryResponses, regionResponses]) => {
                if (!countryResponses.countryProfile) return;
                if (!countryResponses.occupationMonthlyCosts) return;
                if (!countryResponses.workingHours) return;
                if (!regionResponses.annualLivingWages) return;
                if (!regionResponses.quarterlyLivingWages) return;

                updateCountryTariffs(countryResponses, regionResponses);
            }
        );

        watch(
            [
                countryResponses.value,
                regionResponses.value,
                occupationResponses.value,
            ],
            ([countryResponses, regionResponses, occupationResponses]) => {
                if (!countryResponses.countryProfile) return;
                if (!countryResponses.occupationMonthlyCosts) return;
                if (!countryResponses.workingHours) return;
                if (!regionResponses.annualLivingWages) return;
                if (!regionResponses.quarterlyLivingWages) return;
                if (!occupationResponses.gigData) return;

                updateTariffs(
                    countryResponses,
                    regionResponses,
                    occupationResponses
                );
            }
        );

        watch(
            () => yearly.value.livingWages.withCostsAndOverhead,
            (value) => {
                if (value) {
                    const intValue = Math.trunc(parseInt(value));
                    tariffsService
                        .socialSecurityTaxes(
                            countryResponses.value.numericCode,
                            intValue
                        )
                        .then((response) => {
                            apiResponseStore.$patch((state) => {
                                state.countryResponses.socialSecurityTaxes.withOverhead =
                                    response;
                            });
                        });
                } else {
                    apiResponseStore.$patch((state) => {
                        state.countryResponses.socialSecurityTaxes.withOverhead = null;
                    });
                }
            }
        );

        watch(
            () => yearly.value.livingWages.withoutOverhead,
            (value) => {
                if (value) {
                    const intValue = Math.trunc(parseInt(value));
                    tariffsService
                        .socialSecurityTaxes(
                            countryResponses.value.numericCode,
                            intValue
                        )
                        .then((response) => {
                            apiResponseStore.$patch((state) => {
                                state.countryResponses.socialSecurityTaxes.withoutOverhead =
                                    response;
                            });
                        });
                } else {
                    apiResponseStore.$patch((state) => {
                        state.countryResponses.socialSecurityTaxes.withoutOverhead = null;
                    });
                }
            }
        );
    },
};
